import React, { createContext, useContext, useEffect, useState } from "react";
import { User, onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/core/persistence";
import { authService } from "../services/auth/authService";
import { AuthContextType, UserClaims, UserRole, LoginFormData, ParticipationFormData } from "../types/auth/auth.types";

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  claims: null,
  login: async () => false,
  logout: async () => false,
  resetPassword: async () => false,
  setPersistenceType: async () => {},
  sendParticipationForm: async () => false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider component that manages authentication state and operations.
 * Provides login, logout, password reset, and other authentication-related functionality.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<UserClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user claims from the authentication token
  const fetchUserClaims = async (user: User | null, forceRefresh = false): Promise<UserClaims | null> => {
    if (!user) {
      setClaims(null);
      return null;
    }

    try {
      const idTokenResult = await user.getIdTokenResult(forceRefresh);
      const tokenClaims = idTokenResult.claims;

      if (tokenClaims.role) {
        const role = tokenClaims.role as UserRole;
        if (["assimagra", "company", "admin", "get2c"].includes(role)) {
          const newClaims = {
            role,
            companyId: tokenClaims.companyId as string | undefined,
          };

          setClaims(newClaims);
          return newClaims;
        }
      }

      // No valid role found, clear claims
      setClaims(null);
      return null;
    } catch (err) {
      console.error("Error fetching user claims:", err);
      setClaims(null);
      return null;
    }
  };

  // Initializes authentication state listener
  useEffect(() => {
    let unsubscribe: () => void;

    const initializeAuth = async () => {
      try {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          setUser(user);

          if (user) {
            // If we have a user, fetch their claims before marking as ready
            await fetchUserClaims(user, false);
          } else {
            // No user, clear claims
            setClaims(null);
          }

          // Mark as ready after both user and claims are processed
          setLoading(false);
        });
      } catch (err) {
        setError("Failed to initialize authentication");
        setLoading(false);
      }
    };

    initializeAuth();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  /**
   * Authenticates a user with email and password
   */
  const login = async (data: LoginFormData): Promise<boolean> => {
    try {
      setError(null);
      setLoading(true);

      const userCredential = await authService.login(data);
      if (!userCredential) {
        throw new Error("Invalid credentials");
      }

      // Force refresh claims and wait for completion
      await fetchUserClaims(userCredential.user, true);

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to login";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logs out the current user
   */
  const logout = async (): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      await authService.logout();

      // Reset all auth state
      setUser(null);
      setClaims(null);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to logout";
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initiates a password reset for the specified email
   */
  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      setError(null);
      await authService.resetPassword(email);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to reset password";
      setError(errorMessage);
      return false;
    }
  };

  /**
   * Sets the authentication persistence type
   */
  const setPersistenceType = async (rememberMe: boolean): Promise<void> => {
    try {
      await authService.setPersistenceType(rememberMe);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to set persistence type";
      setError(errorMessage);
    }
  };

  const sendParticipationForm = async (data: ParticipationFormData): Promise<boolean> => {
    try {
      await authService.sendParticipationForm(data);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to send participation form";
      setError(errorMessage);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        claims,
        login,
        logout,
        resetPassword,
        setPersistenceType,
        sendParticipationForm,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
